import { Group, useMantineTheme } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { TeamOut } from '@repo/api-codegen';
import {
	useApiBulkUpdateResourceRelations,
	useGetAiAccessStatus,
} from '@repo/api-codegen';
import { useApiListTeamsV2 } from '@repo/api-codegen/v2';
import RichTooltip from '@repo/common/components/RichTooltip/RichTooltip';
import { SELECTABLE_PROPERTY_OPTIONS } from '@repo/common/components/SelectableProperty/constants';
import type { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import { EntityType } from '@repo/common/enums/entityType';
import { pluralize } from '@repo/common/utils';
import type { IconNames } from '@repo/foundations';
import { Banner, Icon, Text } from '@repo/foundations';
import type { SecodaThemeShades } from '@repo/theme/types';
import type { UseMutateAsyncFunction } from '@tanstack/react-query';
import { map, noop, size, startCase } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import type {
	DefaultContext,
	IApiListResponse,
	ISecodaEntity,
	ITag,
	UpdateRequestParams,
} from '../../api';
import {
	ALWAYS_PUBLISHED_ENTITIES,
	queryClient,
	useAuthUser,
	useBulkDeleteSecodaEntities,
	useBulkGenerateAiMetadata,
	useBulkUpdateSecodaEntities,
	useCollectionListAll,
	useDataQualityAccess,
	useSuspenseFeatureAccess,
	useTagList,
	useUpdateSecodaEntity,
} from '../../api';
import type { UpdateModelHook } from '../../api/factories/types';
import { resourceCatalogQueryKeyFactory } from '../../api/hooks/resourceCatalog/constants';
import type { RequestData } from '../../api/hooks/secodaEntity/useBulkUpdateSecodaEntities';
import { useCurrentTeam } from '../../api/hooks/team/myMemberships';
import { useExtendedUserList } from '../../api/hooks/user/useExtendedUserList';
import { useEntityBreadcrumbTeam } from '../../hooks/useEntityBreadcrumbs';
import type { WithOnlyIdRequired } from '../../lib/typescript';
import { useBackgroundJob2 } from '../BackgroundJobProgress/BackgroundJob2.hooks';
import { CATALOG_TYPES_ENABLE_DATA_QUALITY_SCORE } from '../CatalogView/hooks/useColumnDefs';
import type { CatalogServerType } from '../CatalogView/types';
import entityDrawerStore from '../EntityDrawer/store';
import { getOwnerAndGroupSelectorOptions } from '../EntityPageLayout/utils';
import { FilterOptionType } from '../Filter';
import { openConfirmModal, openModal } from '../ModalManager';
import { closeSpotlight } from '../Spotlight';
import type { ICommandListItem } from '../Spotlight/components/CommandPalette/constants';
import {
	CollectionRender,
	DataQualityRender,
	DescriptionRender,
	LastUpdatedRender,
	OwnerRender,
	RelatedEntitiesRender,
	SelectorRender,
	TagRender,
	TitleRender,
} from './render';
import type { ActionProperty, ExtendedDataTableColumn } from './types';

interface UseGenericColumnsProps<T extends ISecodaEntity> {
	useUpdate?: UpdateModelHook<
		T,
		UpdateRequestParams<T>,
		DefaultContext<T>,
		unknown
	>;
	forceReadOnly?: boolean;
}

import { ActionModal } from './SecodaEntityComponents';

export function useGenericColumns<T extends ISecodaEntity>({
	useUpdate = useUpdateSecodaEntity as unknown as UpdateModelHook<
		T,
		UpdateRequestParams<T>,
		DefaultContext<T>,
		unknown
	>,
	forceReadOnly = false,
}: UseGenericColumnsProps<T>): ExtendedDataTableColumn<T>[] {
	const theme = useMantineTheme();
	const { collectionsAccess, secodaCoreAccess } = useSuspenseFeatureAccess();
	const dqsEnabled = useDataQualityAccess();
	const { mutateAsync: update } = useUpdate({});
	const { mutateAsync: updateResourceRelations } =
		useApiBulkUpdateResourceRelations({
			onSuccess: () => {
				showNotification({
					title: 'Success',
					message: 'Resource relations updated',
				});
			},
			onError: () => {
				showNotification({
					title: 'Error',
					message: 'Failed to update resource relations',
				});
			},
		});

	const handleChange = useCallback(
		(field: keyof T) => (id: T['id']) => (value: string | string[] | boolean) =>
			update(
				{
					data: {
						id,
						[field]: value,
					} as unknown as WithOnlyIdRequired<T>,
				},
				{
					onSuccess: () => {},
				}
			),
		[update]
	);

	const columns: ExtendedDataTableColumn<T>[] = useMemo(() => {
		const baseColumns: ExtendedDataTableColumn<T>[] = [
			{
				accessor: 'title',
				titleLabel: 'Title',
				render: (record) => <TitleRender record={record} />,
				filterOptionType: FilterOptionType.TITLE,
				width: 200,
				ellipsis: true,
			},
			{
				navigate: false,
				accessor: 'description',
				titleLabel: 'Description',
				render: (record) => (
					<DescriptionRender
						readOnly={forceReadOnly}
						record={record as ISecodaEntity}
						onChange={handleChange('description')}
						field={'description'}
					/>
				),
				width: 350,
				filterOptionType: FilterOptionType.DESCRIPTION,
			},
			{
				navigate: false,
				accessor: 'owners',
				titleLabel: 'Owners',
				render: (record) => (
					<OwnerRender
						forceReadOnly={forceReadOnly}
						record={record as ISecodaEntity}
						onChangeUserOwners={handleChange('owners')}
						onChangeGroupOwners={handleChange('owners_groups')}
					/>
				),
				width: 200,
				filterOptionType: FilterOptionType.OWNERS,
			},
			{
				navigate: false,
				accessor: 'tags',
				titleLabel: 'Tags',
				render: (record) => (
					<TagRender
						readOnly={forceReadOnly}
						record={record as ISecodaEntity}
						onChange={handleChange('tags')}
					/>
				),
				width: 200,
				filterOptionType: FilterOptionType.TAGS,
			},
			{
				navigate: false,
				accessor: 'collections',
				titleLabel: 'Collections',
				render: (record) => (
					<CollectionRender
						forceReadOnly={forceReadOnly}
						record={record as ISecodaEntity}
						onChange={handleChange('collections')}
					/>
				),
				width: 200,
				filterOptionType: FilterOptionType.COLLECTIONS,
			},
			{
				navigate: false,
				accessor: 'related',
				titleLabel: 'Related',
				render: (record) => (
					<RelatedEntitiesRender
						record={
							record as unknown as ISecodaEntity & {
								related_entities?: ISecodaEntity[];
							}
						}
						field="related_entities"
						onChange={(entityId: string) => (value: (string | boolean)[]) =>
							updateResourceRelations({
								body: {
									from_entity: entityId,
									to_entities: value.map((id) => id as string),
								},
							})
						}
						hideOnEmpty
						forceReadOnly={forceReadOnly}
						defaultOpened
					/>
				),
			},
			{
				accessor: 'pii',
				titleLabel: 'Governance',
				navigate: false,
				render: (record) => (
					<SelectorRender
						record={record as ISecodaEntity}
						accessor={'pii'}
						esAccessor="pii"
						nilOption={false}
						onChange={handleChange('pii')}
						forceReadOnly={forceReadOnly}
					/>
				),
				width: 150,
				filterOptionType: FilterOptionType.PII,
			},
			{
				accessor: 'verified',
				titleLabel: 'Verified',
				navigate: false,
				render: (record) => (
					<SelectorRender
						record={record as ISecodaEntity}
						accessor={'verified'}
						esAccessor="verified"
						nilOption={false}
						onChange={handleChange('verified')}
						forceReadOnly={forceReadOnly}
					/>
				),
				width: 150,
				filterOptionType: FilterOptionType.VERIFICATION,
			},
			{
				accessor: 'published',
				titleLabel: 'Status',
				navigate: false,
				render: (record) => {
					if (ALWAYS_PUBLISHED_ENTITIES.includes(record.entity_type)) {
						return (
							<RichTooltip
								title="Read-only"
								body={
									<Text size="sm">
										{pluralize(startCase(record.entity_type))} are always
										published.
									</Text>
								}
								position="left"
							>
								<Group spacing="3xs">
									<Icon
										name="circleCheckFilled"
										fillColor="icon/success/default"
									/>
									<Text size="sm">Published</Text>
								</Group>
							</RichTooltip>
						);
					}

					if (record.entity_type === EntityType.column) {
						const propertyValue = SELECTABLE_PROPERTY_OPTIONS['published'].find(
							(option) => option.value === record.published
						);

						if (propertyValue) {
							return (
								<RichTooltip
									title="Read-only"
									body={
										<Text size="sm">
											Column status is automatically set based on the parent
											table&apos;s status.
										</Text>
									}
									position="left"
								>
									<Group spacing="3xs">
										<Icon
											name={propertyValue.iconName!}
											color={propertyValue.color}
											style={{
												color: theme.other.getColor(propertyValue.color),
											}}
										/>
										<Text size="sm">{propertyValue.label}</Text>
									</Group>
								</RichTooltip>
							);
						}
					}

					return (
						<SelectorRender
							forceReadOnly={forceReadOnly}
							record={record as ISecodaEntity}
							accessor={'published'}
							esAccessor="published"
							nilOption={false}
							onChange={handleChange('published')}
						/>
					);
				},
				width: 150,
				filterOptionType: FilterOptionType.PUBLISHED,
			},
			{
				accessor: 'updated_at',
				titleLabel: 'Last updated',
				render: (record) => (
					<LastUpdatedRender record={record} field={'updated_at'} />
				),
				width: 200,
				filterOptionType: FilterOptionType.UPDATED_TIME,
			},
		];

		return baseColumns.filter((column) => {
			if (column.accessor === 'collections') {
				return collectionsAccess;
			}

			if (column.accessor === 'pii') {
				return secodaCoreAccess;
			}

			if (column.accessor === 'dqs') {
				return dqsEnabled;
			}

			return true;
		});
	}, [
		forceReadOnly,
		handleChange,
		updateResourceRelations,
		theme.other,
		collectionsAccess,
		secodaCoreAccess,
		dqsEnabled,
	]);

	return columns;
}

export function useDataQualityScoreColumns<T extends ISecodaEntity>({
	catalogType,
}: {
	catalogType?: CatalogServerType;
}) {
	const dqsEnabled = useDataQualityAccess();

	return useMemo(() => {
		if (
			!catalogType ||
			!dqsEnabled ||
			!CATALOG_TYPES_ENABLE_DATA_QUALITY_SCORE.includes(catalogType)
		) {
			return [];
		}

		return [
			{
				accessor: 'dqs',
				titleLabel: 'Quality',
				navigate: false,
				render: (record: T) => <DataQualityRender record={record} />,
				width: 150,
				filterOptionType: FilterOptionType.DATA_QUALITY,
			},
		];
	}, [catalogType, dqsEnabled]);
}

function canModifyPublishedStatus<T extends ISecodaEntity>(
	selectedEntities: T[]
): boolean {
	if (selectedEntities.length === 0) {
		return false;
	}

	const modifiableEntities = selectedEntities.filter((entity) => {
		if (entity.entity_type === EntityType.column) {
			return false;
		}
		if (ALWAYS_PUBLISHED_ENTITIES.includes(entity.entity_type)) {
			return false;
		}
		return true;
	});

	return modifiableEntities.length > 0;
}

export function onClickGenericAction<T extends ISecodaEntity>(
	property: ActionProperty,
	single: boolean,
	bulkUpdateSecodaEntities: UseMutateAsyncFunction<
		string[],
		unknown,
		RequestData,
		unknown
	>,
	withLocalProperties?: SelectablePropertyItem[],
	withSearch?: Record<string, string>
) {
	return async (selected: T[]) => {
		closeSpotlight('bulkActions');
		openModal({
			withCloseButton: false,
			children: (
				<ActionModal<T>
					single={single}
					property={property}
					defaultSelected={selected}
					withLocalProperties={withLocalProperties}
					withSearch={withSearch}
					bulkUpdateSecodaEntities={bulkUpdateSecodaEntities}
				/>
			),
		});
	};
}

export function useAiAction<T>() {
	const { mutateAsync: bulkGenerateAiMetadata } = useBulkGenerateAiMetadata();

	const [startJob] = useBackgroundJob2(
		resourceCatalogQueryKeyFactory.allLists(),
		'AI description generation completed'
	);

	const { data } = useGetAiAccessStatus({});

	const handleOnClick = useCallback(
		(selected: T[]) => {
			openConfirmModal({
				variant: 'default',
				title: `Generate AI description for ${selected.length} selected resources?`,
				children: (
					<Text size="md" pt="md">
						{data?.restriction_info.is_restricted ? (
							<Banner message={data?.restriction_info.reason} tone="warning" />
						) : (
							<>
								This will overwrite any existing descriptions. This can&apos;t
								be undone.
							</>
						)}
					</Text>
				),
				labels: {
					cancel: 'Cancel',
					confirm: 'Generate',
				},
				confirmProps: {
					disabled: data?.restriction_info.is_restricted,
					variant: 'primary',
				},
				onConfirm: async () => {
					const { jobId } = await bulkGenerateAiMetadata({
						data: {
							field: 'description',
							entity_ids: map(selected, 'id'),
							bulk_generate: true,
						},
					});
					startJob(jobId);
				},
			});
		},
		[
			bulkGenerateAiMetadata,
			data?.restriction_info.is_restricted,
			data?.restriction_info.reason,
			startJob,
		]
	);

	return useMemo(
		() => ({
			id: 'actions::ai',
			title: 'Apply AI description',
			name: 'Apply AI description',
			iconName: 'sparkles' as IconNames,
			hotkey: '/ai',
			type: EntityType.all,
			team: undefined,
			category: 'actions',
			show:
				data?.restriction_info.restriction_type !==
				'workspace_settings_disabled',
			onClick: handleOnClick,
		}),

		[data?.restriction_info.restriction_type, handleOnClick]
	);
}

/**
 * Compute confirmation modal copy for bulk delete/removal actions.
 *
 * Decision rules (no mixed operations in one run):
 * - Remove from team:
 *   - Applies when ALL selected resources:
 *     - Have `teams` that include the current team (from team breadcrumb or URL), and
 *     - Are shared with more than one team (teams.length > 1)
 *   - Result: Detaches the resources from the current team only. Content remains in other teams.
 *
 * - Permanently delete (workspace):
 *   - Applies in all other cases (including when any selected resource belongs only to the current team
 *     or current team context is missing).
 *   - Result: Permanently removes the resources from the workspace.
 *
 * Notes:
 * - This is used by the bulk Actions menu in TableV2. It evaluates the current team context
 *   (`teamBreadcrumb ?? currentTeam.id`) and the selection to choose a single, clear action.
 * - The confirm button label mirrors the chosen operation ("Remove from team" or "Delete").
 */
function computeDeleteConfirmCopy<T extends ISecodaEntity>(
	selectedForCopy: Array<T & { teams?: string[] }>,
	currentTeamIdForCopy?: string,
	textChildSuffixForCopy: string = ''
): { title: string; body: string; confirmLabel: string } {
	const canAllDetach =
		!!currentTeamIdForCopy &&
		selectedForCopy.length > 0 &&
		selectedForCopy.every(
			(item) =>
				Array.isArray(item.teams) &&
				item.teams.includes(currentTeamIdForCopy) &&
				item.teams.length > 1
		);

	if (canAllDetach) {
		return {
			title: 'Remove from team',
			body:
				selectedForCopy.length === 1
					? 'This resource is shared across multiple teams. Remove it from the current team only?'
					: `These (${selectedForCopy.length}) resources are shared across multiple teams. Remove them from the current team only?`,
			confirmLabel: 'Remove from team',
		};
	}

	const firstEntityType = selectedForCopy[0]?.entity_type;
	const allSameType =
		firstEntityType &&
		selectedForCopy.every((item) => item.entity_type === firstEntityType);

	let entityTypeLabel: string;
	if (allSameType && firstEntityType) {
		entityTypeLabel =
			selectedForCopy.length === 1
				? startCase(firstEntityType)
				: pluralize(startCase(firstEntityType));
	} else if (selectedForCopy.length === 1) {
		entityTypeLabel = 'item';
	} else {
		entityTypeLabel = 'items';
	}

	return {
		title: `Delete ${entityTypeLabel}${textChildSuffixForCopy} from your workspace?`,
		body: "This can't be undone.",
		confirmLabel: `Delete ${entityTypeLabel}`,
	};
}

export function useGenericActions<
	T extends ISecodaEntity,
>(): ICommandListItem<T>[] {
	const { isEditorOrAdminUser } = useAuthUser();

	const aiAction = useAiAction<T>();

	const handleEditInSidebar = useCallback(
		(id: string) => {
			entityDrawerStore
				.openEntityDrawerById(isEditorOrAdminUser, id, undefined, () => {
					queryClient.invalidateQueries({
						queryKey: resourceCatalogQueryKeyFactory.all(),
					});
				})
				.then();
		},
		[isEditorOrAdminUser]
	);

	const { mutateAsync: bulkUpdateSecodaEntities } =
		useBulkUpdateSecodaEntities();

	const { mutateAsync: bulkDeleteSecodaEntities } = useBulkDeleteSecodaEntities(
		{ name: 'Entities' }
	);

	const { currentTeam } = useCurrentTeam();
	const { teamBreadcrumb } = useEntityBreadcrumbTeam();

	const { activeUsers, userGroups } = useExtendedUserList();

	const usersOptions = useMemo(
		() => getOwnerAndGroupSelectorOptions(activeUsers ?? [], [], noop, noop),
		[activeUsers]
	);

	const userGroupOptions = useMemo(
		() => getOwnerAndGroupSelectorOptions([], userGroups ?? [], noop, noop),
		[userGroups]
	);

	const { data: collectionData } = useCollectionListAll();

	const collections = useMemo(
		() =>
			collectionData?.results?.map((collection) => ({
				label: collection.title || 'Untitled',
				value: collection.id,
				icon: collection.icon,
			})) ?? [],
		[collectionData?.results]
	);

	const { data: tags } = useTagList({
		options: {
			select: (data: IApiListResponse<ITag>) =>
				data.results.map(
					(tag) =>
						({
							label: tag.name,
							value: tag.id,
							color: tag.color as SecodaThemeShades,
						}) as SelectablePropertyItem
				),
		},
	});

	const { data: teamsData } = useApiListTeamsV2({});

	const teamOptions = useMemo(
		() =>
			teamsData?.map((team: TeamOut) => ({
				label: team.name,
				value: team.id,
				icon: team.icon,
			})) ?? [],
		[teamsData]
	);

	const actions = useMemo(
		() => [
			{
				id: 'actions::collections',
				title: 'Set collections',
				name: 'Set collections',
				iconName: 'folders' as IconNames,
				hotkey: '/sc',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: onClickGenericAction(
					'collections',
					false,
					bulkUpdateSecodaEntities,
					collections
				),
			},
			{
				id: 'actions::tags',
				title: 'Set tags',
				name: 'Set tags',
				iconName: 'tag' as IconNames,
				hotkey: '/st',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: onClickGenericAction(
					'tags',
					false,
					bulkUpdateSecodaEntities,
					tags ?? []
				),
			},
			{
				id: 'actions::owners',
				title: 'Set user owners',
				name: 'Set user owners',
				iconName: 'user' as IconNames,
				hotkey: '/us',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: onClickGenericAction(
					'owners',
					false,
					bulkUpdateSecodaEntities,
					usersOptions
				),
			},
			{
				id: 'actions::owners_groups',
				title: 'Set group owners',
				name: 'Set group owners',
				iconName: 'userCircle' as IconNames,
				hotkey: '/gp',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: onClickGenericAction(
					'owners_groups',
					false,
					bulkUpdateSecodaEntities,
					userGroupOptions
				),
			},
			{
				id: 'actions::owners_remove',
				title: 'Remove user owners',
				name: 'Remove user owners',
				iconName: 'user' as IconNames,
				hotkey: '/ru',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: async (selected: T[]) => {
					openConfirmModal({
						title: 'Remove owners',
						children: `Are you sure you want to remove all User and Group Owners from the (${selected.length}) selected resource${selected.length > 1 ? 's' : ''}?`,
						labels: {
							cancel: 'Cancel',
							confirm: 'Remove',
						},
						onConfirm: async () => {
							const data = selected.map((entity) => ({
								id: entity.id,
								data: {
									owners: [],
									owners_groups: [],
								},
							}));
							await bulkUpdateSecodaEntities(
								{
									data,
								},
								{
									onSuccess: () => {
										closeSpotlight('bulkActions');
									},
								}
							);
						},
					});
				},
			},
			{
				id: 'actions::teams',
				title: 'Set teams',
				name: 'Set teams',
				iconName: 'users' as IconNames,
				hotkey: '/tm',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: onClickGenericAction(
					'teams',
					false,
					bulkUpdateSecodaEntities,
					teamOptions
				),
			},
			{
				id: 'actions::published',
				title: 'Set published',
				name: 'Set published',
				iconName: 'circle' as IconNames,
				hotkey: '/mp',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: (selected: T[]) => canModifyPublishedStatus(selected),
				onClick: onClickGenericAction(
					'published',
					true,
					bulkUpdateSecodaEntities,
					SELECTABLE_PROPERTY_OPTIONS.published
				),
			},
			{
				id: 'actions::pii',
				title: 'Set PII',
				name: 'Set PII',
				iconName: 'shieldLockUnfilled' as IconNames,
				hotkey: '/mp',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: onClickGenericAction(
					'pii',
					true,
					bulkUpdateSecodaEntities,
					SELECTABLE_PROPERTY_OPTIONS.pii
				),
			},
			{
				id: 'actions::verified',
				title: 'Set verification',
				name: 'Set verification',
				iconName: 'discountCheck' as IconNames,
				hotkey: '/vf',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: true,
				onClick: onClickGenericAction(
					'verified',
					true,
					bulkUpdateSecodaEntities,
					SELECTABLE_PROPERTY_OPTIONS.verified
				),
			},
			{
				id: 'actions::sidebar',
				title: 'Edit in sidebar',
				name: 'Edit in sidebar',
				iconName: 'layoutSidebarRightExpand' as const,
				hotkey: '/sb',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				show: (selected: T[]) => size(selected) === 1,
				onClick: async (selected: T[]) => {
					if (size(selected) === 1) {
						closeSpotlight('bulkActions');
						handleEditInSidebar(selected[0].id);
					}
				},
			},
			aiAction,
			{
				id: 'actions::delete',
				title: 'Delete',
				name: 'Delete',
				show: true,
				iconName: 'trash' as const,
				hotkey: '/dq',
				type: EntityType.all,
				team: undefined,
				category: 'actions',
				onClick: async (selected: T[], clearSelected: VoidFunction) => {
					let textChildSuffix = '';

					const someHaveChildren = selected.some(
						(item) => item.has_child_of_same_type
					);

					if (someHaveChildren) {
						if (selected.length === 1) {
							textChildSuffix = ' and its children';
						} else {
							textChildSuffix = ' and their children';
						}
					}

					const currentTeamIdForCopy = teamBreadcrumb ?? currentTeam?.id;
					const selectedWithTeamsForCopy = selected as Array<
						T & { teams?: string[] }
					>;
					const {
						title: confirmTitle,
						body: confirmBody,
						confirmLabel,
					} = computeDeleteConfirmCopy<T>(
						selectedWithTeamsForCopy,
						currentTeamIdForCopy,
						textChildSuffix
					);

					openConfirmModal({
						tone: 'critical',
						title: confirmTitle,
						children: confirmBody,
						confirmLabel,
						confirmProps: {
							variant: 'primary',
							tone: 'critical',
						},
						labels: {
							cancel: 'Cancel',
							confirm: confirmLabel,
						},
						onConfirm: async () => {
							const currentTeamId = teamBreadcrumb ?? currentTeam?.id;
							const selectedWithTeams = selected as Array<
								T & { teams?: string[] }
							>;
							const canAllDetach =
								!!currentTeamId &&
								selectedWithTeams.length > 0 &&
								selectedWithTeams.every(
									(item) =>
										Array.isArray(item.teams) &&
										item.teams.includes(currentTeamId) &&
										item.teams.length > 1
								);

							if (canAllDetach) {
								await bulkUpdateSecodaEntities({
									data: selectedWithTeams.map((entity) => ({
										id: entity.id,
										data: {
											teams: (entity.teams || []).filter(
												(t) => t !== currentTeamId
											),
										},
									})),
								});
							} else {
								await bulkDeleteSecodaEntities({
									data: selectedWithTeams.map(({ id }) => ({ id })),
								});
							}

							clearSelected();
							closeSpotlight('bulkActions');

							queryClient.invalidateQueries({
								queryKey: resourceCatalogQueryKeyFactory.allLists(),
							});
							queryClient.invalidateQueries({
								queryKey: resourceCatalogQueryKeyFactory.all(),
							});
						},
					});
				},
			},
		],
		[
			aiAction,
			bulkDeleteSecodaEntities,
			bulkUpdateSecodaEntities,
			collections,
			handleEditInSidebar,
			tags,
			teamOptions,
			userGroupOptions,
			usersOptions,
			teamBreadcrumb,
			currentTeam,
		]
	);

	return actions;
}
